import Form from "react-bootstrap/Form";
import FormInput from "./FormInput";
import { Button } from "../Buttons/Button";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useRef, useState, useEffect } from "react";
import { Recaptcha } from "../../components/Recaptcha";
import PasswordStrength from "../../components/PasswordStrength";
import ToggleButtons from "../Buttons/ToggleButtons";
import { FormGroup } from "react-bootstrap";
import { previousFifteenDays, todayFormatted, YoutubeEmbed, isSafeUrl } from "../../utils";
import { useAuth } from "../../components/AuthContext/AuthContext";
import PromptIfDirty from "./PromptIfDirty";
import { useMediaQuery } from "react-responsive";
import DOMPurify from 'dompurify';

const PHONE_META_KEY = /^(.*phone.*)_(type|country|iso2)$/i;
const PHONE_KEY = /phone/i;

const normalizeForDirtyCheck = (values = {}) =>
  Object.keys(values)
    .filter((key) => key !== "undefined" && !PHONE_META_KEY.test(key))
    .sort()
    .reduce((acc, key) => {
      const value = values[key] ?? "";
      acc[key] = PHONE_KEY.test(key)
        ? String(value).replace(/[^\d+]/g, "")
        : value;
      return acc;
    }, {});


const isSamePhone = (current = "", initial = "") => {
  if (current === initial) return true;
  if (initial.startsWith("+")) return false;
  const initialDigits = initial.replace(/^0+/, "");
  return !!initialDigits && current.replace(/^\+/, "").endsWith(initialDigits);
};

export const isFormDirty = (formik) => {
  if (!formik) return false;
  const current = normalizeForDirtyCheck(formik.values);
  const initial = normalizeForDirtyCheck(formik.initialValues);
  const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
  return [...keys].some((key) =>
    PHONE_KEY.test(key)
      ? !isSamePhone(current[key] ?? "", initial[key] ?? "")
      : JSON.stringify(current[key] ?? "") !== JSON.stringify(initial[key] ?? ""),
  );
};
/**
 *
 *
 * @param {Array} fields - Array of form fields
 * @param {Object} initialValues - Initial values of the form
 * @param {Object} auth - Auth context
 * @returns {Array} - Modified fields
 */

export const modifyFields = (fields, initialValues, auth, props) => {
  const isFemale = props?.sex === "female";
  const conditionHandlers = [
    {
      condition: (field, initialValues, auth, props) =>
        field.label === "Are you pregnant?",
      modify: (field, initialValues, auth, props) => {
        const isFemale = props?.sex === "female";
        return {
          ...field,
          hide: !isFemale,
          required: isFemale,
          disabled: !isFemale,
          children: Array.isArray(field.children)
            ? field.children.map((child) => ({
              ...child,
              hide: !isFemale,
            }))
            : field.children,
        };
      },
    },
    {
      condition: (field, initialValues) =>
        initialValues?.ispregnant === "Yes" &&
        isFemale &&
        field.name === "trimester",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Smoking-related
    {
      condition: (field, initialValues) =>
        initialValues?.smoking === "Yes" && field.name === "cigarettes_per_day",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.smoking === "Ex-smoker" && field.name === "how_many_year",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },

    {
      condition: (field, initialValues) =>
        initialValues?.smoking === "Ex-smoker" &&
        field.name === "how_many_day_average",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Alcohol-related
    {
      condition: (field, initialValues) =>
        initialValues?.alcohol === "Yes" &&
        field.name === "alcohol_units_per_week",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Physical activity helpText
    {
      condition: (field, initialValues) =>
        initialValues?.physical_activity === "Yes" &&
        field.name === "physical_activity",
      modify: (field) => ({
        ...field,
        // helpText: "Please tell us how many hours you spent on physical activity last week"
      }),
    },
    // Physical activity hours
    {
      condition: (field, initialValues) =>
        initialValues?.physical_activity === "Yes" &&
        [
          "light_activity_hours_per_week",
          "moderate_activity_hours_per_week",
          "vigorous_activity_hours_per_week",
        ].includes(field.name),
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Sexually active
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "No" &&
        field.name === "unprotected_sex_type",
      modify: (field) => ({
        ...field,
        disabled: true,
        check_group_required: false,
        check_group_hide: true,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "Yes" &&
        [
          "partners",
          "recent_sex",
          "had_unprotected_sex",
          "sti_diagnosed",
          "sti_symptoms",
          "potential_sti_exposure",
        ].includes(field.name),
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Unprotected sex type
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "Yes" &&
        initialValues?.had_unprotected_sex === "Yes" &&
        field.name === "unprotected_sex_type",
      modify: (field) => ({
        ...field,
        disabled: false,
        check_group_required: true,
        check_group_hide: false,
      }),
    },
    // STI symptoms
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "Yes" &&
        initialValues?.sti_symptoms === "Yes" &&
        field.name === "sti_symptoms_description",
      modify: (field) => ({
        ...field,
        disabled: false,
        required: true,
        hide: false,
      }),
    },
    // Potential STI exposure
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "Yes" &&
        initialValues?.potential_sti_exposure === "Yes" &&
        field.name === "potential_sti_exposure_type",
      modify: (field) => ({
        ...field,
        check_group_required: true,
        check_group_hide: false,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.sexually_active === "Yes" &&
        initialValues?.potential_sti_exposure === "Yes" &&
        Array.isArray(initialValues?.potential_sti_exposure_type) &&
        initialValues?.potential_sti_exposure_type.includes("Other") &&
        field.label === "Please specify other STI exposure type",
      modify: (field) => ({
        ...field,
        hide: false,
        check_group_required: true,
        check_group_hide: false,
      }),
    },
    // Diagnosed conditions checkboxes
    {
      condition: (field, initialValues) =>
        initialValues?.was_diagnosed === "Yes" &&
        field.check_box_group &&
        field.name === "condition",
      modify: (field) => ({
        ...field,
        check_box_group: field.check_box_group.map((innerField) => ({
          ...innerField,
          disabled: false,
          hide: false,
        })),
      }),
    },
    // Medications checkboxes
    {
      condition: (field, initialValues) =>
        field.name === "medications" &&
        field.check_box_group &&
        initialValues?.medication === "Yes",
      modify: (field) => ({
        ...field,
        check_box_group: field.check_box_group.map((innerField) => ({
          ...innerField,
          disabled: false,
          hide: false,
        })),
      }),
    },

    {
      condition: (field, initialValues) =>
        field.name === "medications" &&
        field.check_box_group &&
        initialValues?.medication !== "Yes",
      modify: (field) => ({
        ...field,
        check_box_group: field.check_box_group.map((innerField) => ({
          ...innerField,
          disabled: true,
          hide: true,
        })),
      }),
    },

    // Other condition description
    {
      condition: (field, initialValues) =>
        initialValues?.was_diagnosed === "Yes" &&
        Array.isArray(initialValues?.condition) &&
        initialValues?.condition.includes("Cancer") &&
        field.label ===
        "Please specify cancer type and whether you are currently or recently (past 3 months) under treatment (chemo, radio, or immunotherapy).",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.was_diagnosed === "Yes" &&
        Array.isArray(initialValues?.condition) &&
        initialValues?.condition.includes("Vitamin/mineral deficiency") &&
        field.label === "Please specify which deficiency",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.was_diagnosed === "Yes" &&
        Array.isArray(initialValues?.condition) &&
        initialValues?.condition.includes("Other") &&
        field.placeholder === "Please specify conditions here",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },

    // Other medication description
    {
      condition: (field, initialValues) =>
        initialValues?.medication === "Yes" &&
        Array.isArray(initialValues?.medications) &&
        initialValues?.medications.includes("Cholesterol-lowering") &&
        field.label ===
        "Please tell us the name, dosage, and frequency of your medication." &&
        field.name === "specify_medication",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.medication === "Yes" &&
        Array.isArray(initialValues?.medications) &&
        initialValues?.medications.includes("Other") &&
        field.label === "Please specify" &&
        field.controlId === "24",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },

    // Supplements checkboxes
    {
      condition: (field, initialValues) =>
        initialValues?.taking_supplements === "Yes" &&
        field.check_box_group &&
        field.name === "supplements",
      modify: (field) => ({
        ...field,
        check_box_group: field.check_box_group.map((innerField) => ({
          ...innerField,
          disabled: false,
          hide: false,
        })),
      }),
    },
    // Other supplement description
    {
      condition: (field, initialValues) =>
        initialValues?.taking_supplements === "Yes" &&
        Array.isArray(initialValues?.supplements) &&
        initialValues?.supplements.includes("Other") &&
        field.label === "Please specify" &&
        field.controlId === "11",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },

    {
      condition: (field, initialValues) =>
        initialValues?.taking_supplements === "Yes" &&
        Array.isArray(initialValues?.supplements) &&
        initialValues?.supplements.includes("Iron, Folate") &&
        field.label ===
        "Please tell us the name, dosage, and frequency of your medication." &&
        field.name === "supplement_details",
      modify: (field) => ({
        ...field,
        disabled: false,
        hide: false,
        required: true,
      }),
    },
    // Manual address entry
    {
      condition: (field, initialValues) =>
        initialValues?.home_street_address === "No results" &&
        ["address_line_1", "address_line_2", "home_city"].includes(field.name),

      modify: (field) => ({
        ...field,
        disabled: field.disabled,
        required: true,
        hide: false,
      }),
    },
    {
      condition: (field, initialValues) =>
        initialValues?.home_street_address === "No results" &&
        field.name === "home_country",

      modify: (field) => ({
        ...field,
        hide: false,
        disabled: true,
        readOnly: true,
        required: false,
      }),
    },
  ];
  const applyModifications = (fieldsList) => {
    return fieldsList?.map((field) => {
      // Apply modifications to current field
      let modifiedField = field;
      for (const handler of conditionHandlers) {
        if (handler.condition(modifiedField, initialValues, auth, props)) {
          modifiedField = handler.modify(
            modifiedField,
            initialValues,
            auth,
            props,
          );
        }
      }

      // Recursively apply to children
      if (modifiedField.children && Array.isArray(modifiedField.children)) {
        modifiedField = {
          ...modifiedField,
          children: applyModifications(modifiedField.children),
        };
      }

      // Recursively apply to check_box_group
      if (
        modifiedField.check_box_group &&
        Array.isArray(modifiedField.check_box_group)
      ) {
        modifiedField = {
          ...modifiedField,
          check_box_group: applyModifications(modifiedField.check_box_group),
        };
      }

      return modifiedField;
    });
  };

  return applyModifications(fields);
};

/**
 * Dynamic form component
 * @param {Object} props - Component props
 * @param {Array} props.fields - Array of form fields
 * @param {Object} props.validationSchema - Form validation schema
 * @param {Function} props.onSubmit - Form submit handler
 * @param {Object} props.initialValues - Initial form values
 * @param {Boolean} props.modify - Modify fields based on other field values
 * @param {String} props.className - Additional class name
 * @param {Boolean} props.blockRefresh - Block refresh if form is dirty
 * @param {Boolean} props.btnSecondary - Use secondary button style
 * @param {Boolean} props.genderButton
 * @param {String} props.password - Password to check strength
 * @returns {JSX.Element} - Dynamic form component
 */

const DynamicForm = (props) => {
  const auth = useAuth();
  const recaptchaRef = useRef(null);
  const [toggleValue, setToggleValue] = useState(null);
  const [heightUnitValue, setHeightUnitValue] = useState("metric");
  const [weightUnitValue, setWeightUnitValue] = useState("metric");
  const [genderIdentity, setGenderIdentity] = useState(null);
  const [formData, setFormData] = useState(null);
  const [modifiedFields, setModifiedFields] = useState([]); // Track modified fields in state
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const handleCheckboxChange = (e, groupName, values, setFieldValue) => {
    const { value, checked } = e.target;
    const updatedValues = values[groupName] ? [...values[groupName]] : [];

    if (checked) {
      // Add the value if checked and not already in the array
      if (!updatedValues?.includes(value)) {
        updatedValues.push(value);
      }
    } else {
      // Remove the value if unchecked
      const index = updatedValues.indexOf(value);
      if (index > -1) {
        updatedValues.splice(index, 1);
      }
    }

    // Update the Formik state
    setFieldValue(groupName, updatedValues);
  };

  const generateInitialValues = (fields, data) => {
    const initialValues = {};
    fields?.forEach((field) => {
      if (field.type !== "button" || field.type !== "submit") {
        initialValues[field.name] =
          data?.[field.name] === undefined ? "" : data[field.name];
      }
    });
    return initialValues;
  };

  useEffect(() => {
    // Safely destructure formData, defaulting to an empty object if formData is null or undefined
    const {
      ispregnant = "",
      smoking = "",
      alcohol = "",
      physical_activity = "",
      sexually_active = "",
      had_unprotected_sex = "",
      sti_symptoms = "",
      potential_sti_exposure = "",
      potential_sti_exposure_type = [],
      height = "",
      weight = "",
      heightUnits = "",
      weightUnits = "",
      units = "",
      condition = [],
      medications = [],
      supplements = [],
      home_street_address = "",
    } = formData || {};

    const isModified = () => {
      return (
        ispregnant === "Yes" ||
        smoking === "Yes" ||
        smoking === "Ex-smoker" ||
        alcohol === "Yes" ||
        physical_activity === "Yes" ||
        sexually_active === "Yes" ||
        had_unprotected_sex === "Yes" ||
        sti_symptoms === "Yes" ||
        potential_sti_exposure === "Yes" ||
        potential_sti_exposure_type?.includes("Other") ||
        height ||
        weight ||
        heightUnits ||
        weightUnits ||
        units ||
        condition?.includes("Other") ||
        condition?.includes("Cancer") ||
        condition?.includes("Vitamin/mineral deficiency") ||
        medications?.includes("Other") ||
        medications?.includes("Cholesterol-lowering") ||
        supplements?.includes("Other") ||
        home_street_address === "No results" ||
        supplements?.includes("Iron, Folate") ||
        props?.sex === "female"
      );
    };
    if (isModified()) {
      setModifiedFields((prev) => {
        const newFields = modifyFields(props.fields, formData, auth, props);
        return prev === newFields ? prev : newFields;
      });
    } else {
      setModifiedFields((prev) => {
        return prev === props.fields ? prev : props.fields;
      });
    }

    if (props.initialValues?.heightUnits) {
      setHeightUnitValue(props.initialValues.heightUnits);
    }

    if (props.initialValues?.weightUnits) {
      setWeightUnitValue(props.initialValues.weightUnits);
    }

    // Sex
    if (props.initialValues?.sex) {
      setToggleValue((prev) =>
        prev === props.initialValues.sex ? prev : props.initialValues.sex,
      );
    }

    // Gender identity
    const identifyWithGender = props.initialValues?.identify_with_gender;
    if (["string", "boolean"].includes(typeof identifyWithGender)) {
      setGenderIdentity((prev) => {
        const newGenderIdentity = props.initialValues.identify_with_gender
          ? "yes"
          : "no";
        return prev === newGenderIdentity ? prev : newGenderIdentity;
      });
    }
  }, [
    formData?.ispregnant,
    formData?.smoking,
    formData?.alcohol,
    formData?.physical_activity,
    formData?.sexually_active,
    formData?.had_unprotected_sex,
    formData?.sti_symptoms,
    formData?.potential_sti_exposure,
    formData?.potential_sti_exposure_type,
    formData?.height,
    formData?.weight,
    formData?.heightUnits,
    formData?.weightUnits,
    formData?.units,
    formData?.condition,
    formData?.medications,
    formData?.supplements,
    formData?.home_street_address,
    props.initialValues?.sex,
    props.initialValues?.identify_with_gender,
    props.fields,
    props.sex,
  ]);

  const fields = modifiedFields.length > 0 ? modifiedFields : props.fields; // Use modifiedFields if available
  const splitToggleAndInput = (children) => {
    let toggleField = null;
    let inputField = null;

    for (const child of children) {
      if (child.type === "toggle-buttons") {
        toggleField = child;
      } else {
        inputField = child;
      }
    }

    return { toggleField, inputField };
  };
  return (
    <Formik
      innerRef={props?.formikRef}
      validationSchema={props.validationSchema}
      onSubmit={(values, { resetForm }) => {
        delete values.undefined;
        props.onSubmit?.(values);
        if (props.isResetForm){
           resetForm();
          }
        setTimeout(() => {
          if (recaptchaRef.current) {
            recaptchaRef.current.resetRecaptcha();
          }
        }, 3000);
      }}
      initialValues={{
        ...(props.initialValues ||
          generateInitialValues(props.modify ? modifiedFields : props.fields)),
      }}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={props?.enableReinitializeValue}
    >
      {({
        handleSubmit,
        handleChange,
        values,
        touched,
        errors,
        setFieldValue,
      }) => {
        setFormData(values);

        const formContext = {
          values,
          touched,
          errors,
          handleChange,
          setFieldValue,
          props,
          auth,
        };

        return (
          <>
          <Form
            id="dynamic-form"
            noValidate
            onSubmit={handleSubmit}
            data-testid="form"
            className={`${props.className} create-account-form-wrapper`}
          >
            {props.blockRefresh && <PromptIfDirty />}
            {fields?.map((field, index) => {
              if (field.name === "sample_collection_time") {
                return null;
              }

              if (field.type === "iframe") {
                return (
                  <div key={field.controlId} className="mt-4 mb-4">
                    <Form.Group
                      key={field.controlId}
                      controlId={field.controlId}
                    >
                      <YoutubeEmbed embedId={field.embedId} />
                    </Form.Group>
                  </div>
                );
              }
              if (
                field.type === "section-card" &&
                Array.isArray(field.children)
              ) {
                return (
                  <>
                    {field.title && (
                      <h1 className="section-card-title mb-4">{field.title}</h1>
                    )}
                    <div
                      key={field.controlId || index}
                      className={field.className}
                    >
                      <div className="section-card-content">
                        {field.children.map((child) => {
                          // Handle inline-group children
                          if (
                            child.type === "inline-group" &&
                            Array.isArray(child.children)
                          ) {
                            const { toggleField, inputField } =
                              splitToggleAndInput(child.children);
                            return (
                                
                                !child.hide && (
                                  <div
                                    key={child.controlId || `${index}-child`}
                                    className={child.className}
                                  >
                                    <Form.Group className="inline-group-wrapper">
                                      <div className="d-flex align-items-center justify-content-left inline-label">
                                        {child.label && (
                                          <Form.Label className="labels inline-group-label mb-0 me-4">
                                            {child.label}
                                            {child.required && (
                                              <span className="required">
                                                *
                                              </span>
                                            )}
                                          </Form.Label>
                                        )}
                                        {toggleField && (
                                          <div className="inline-toggle mb-0">
                                            {renderFormField(
                                              toggleField,
                                              formContext,
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      {errors[child.name] && (
                                        <div className="invalid-feedback d-block mb-2">
                                          {errors[child.name]}
                                        </div>
                                      )}

                                      {inputField && (
                                        <div className="inline-group-input">
                                          {renderFormField(
                                            inputField,
                                            formContext,
                                          )}
                                        </div>
                                      )}
                                    </Form.Group>
                                  </div>
                                )
                            );
                          }

                          return (
                            <Form.Group
                              key={child.controlId}
                              controlId={child.controlId}
                            >
                              {renderFormField(child, formContext)}
                              {renderButtonGroup(child, auth, props)}
                              {renderCheckboxGroup(child, formContext)}
                            </Form.Group>
                          );
                        })}
                      </div>
                    </div>
                  </>
                );
              }

              if (field.type === "group" && Array.isArray(field.children)) {

                return (
                  <div key={field.controlId} className={field.className}>
                    <div className="d-flex align-items-center">
                      {field.label && (
                        <h3 className="register-label">
                          {field.label}
                          {field.required && (
                              <span className="required">*</span>
                          )}
                        </h3>
                      )}
                    </div>
                    <div className="account-details-wrapper">
                      {field.children.map((child) => (
                        <Form.Group
                          key={child.controlId}
                          className={child.className}
                        >
                          {renderFormField(child, formContext)}
                          {renderButtonGroup(child, auth, props)}  
                          {renderCheckboxGroup(child, formContext)}
                          {child.name === "password" &&
                            child.type === "password" &&
                            props.password && (
                              <PasswordStrength password={props.password} />
                            )}
                        </Form.Group>
                      ))}
                    </div>
                  </div>
                );
              }

              if (
                field.type === "inline-group" &&
                Array.isArray(field.children)
              ) {
                const toggleField = field.children.find(
                  (child) => child.type === "toggle-buttons",
                );

                const inputField = field.children.find(
                  (child) => child.type !== "toggle-buttons",
                );

                return (
                  <>
                    {!field.hide && (
                      <div
                        key={field.controlId || index}
                        className={field.className}
                      >
                        <Form.Group className="inline-group-wrapper">
                          <div className="d-flex align-items-center justify-content-left inline-label">
                            {field.label && (
                              <Form.Label className="labels inline-group-label mb-0 me-4">
                                {field.label}
                                {field.required && (
                                  <span className="required">*</span>
                                )}
                              </Form.Label>
                            )}

                            {toggleField && (
                              <div className="inline-toggle mb-0">
                                {renderFormField(toggleField, formContext)}
                              </div>
                            )}
                          </div>

                          {errors[field.name] && (
                            <div className="invalid-feedback d-block mb-2">
                              {errors[field.name]}
                            </div>
                          )}

                          {/* Input Row */}
                          {inputField && (
                            <div className="inline-group-input">
                              {renderFormField(inputField, formContext)}
                            </div>
                          )}
                        </Form.Group>
                      </div>
                    )}
                  </>
                );
              }

              return (
                <Form.Group key={field.controlId} controlId={field.controlId}>
                  {renderFormField(field, formContext)}
                  {renderButtonGroup(field, auth, props)}  
                  {renderCheckboxGroup(field, formContext)}
                  {field.name === "password" &&
                    field.type === "password" &&
                    props.password && (
                      <PasswordStrength password={props.password} />
                    )}
                </Form.Group>
              );
            })}
          </Form>
          </>
        );
      }}
    </Formik>
  );

  /**
   * Render form field based on field type
   * @param {Object} field - Form field object
   * @param {Object} context - Form context
   * @returns {JSX.Element} - Form field component
   */

  function renderFormField(field, context) {
    const {
      values,
      touched,
      errors,
      handleChange,
      setFieldValue,
      props,
      auth,
    } = context;
    // Handle button type fields
    if (field.type === "button" || field.type === "submit") {
      return renderButtonField(field);
    }

    // Handle recaptcha fields
    if (field.type === "recaptcha") {
      return (
        <Recaptcha
          key={field.controlId}
          ref={recaptchaRef}
          errors={errors}
          onChange={(response) => setFieldValue("recaptcha_token", response)}
          className="recaptcha-container"
        />
      );
    }

    // Handle button groups
    if (field.type === "button-group") {
      return renderButtonGroupField(field, props, auth);
    }

    // Handle toggle buttons
    if (field.type === "toggle-buttons") {
      
      return renderToggleButtonField(
        field,
        touched,
        errors,
        setFieldValue,
        values,
      );
    }

    // Handle help text
    if (field.type === "help-text") {
      return (
        <small className="form-text text-muted mt-2">{field.helpText}</small>
      );
    }

    // Handle sample collection date
    if (field.name === "sample_collection_date") {
      return renderSampleCollectionField(
        field,
        props,
        values,
        touched,
        errors,
        handleChange,
      );
    }

    // Handle consent steps
    if (field.name === "consent_steps") {
      return renderConsentField(field, props);
    }

    return renderDefaultField(
      field,
      values,
      touched,
      errors,
      handleChange,
      props,
      setFieldValue,
    );
  }

  /**
   * Render button field
   * @param {Object} field - Button field object
   * @returns {JSX.Element} - Button field component
   */
  function renderButtonField(field) {
    let justifyContent = field.linkName ? "between" : "end";
    const justifyClass = field.className?.includes("btn-phlebotomist")
    ? "justify-content-end"
    : `justify-content-${justifyContent}`;
    return (
      <>
        <div
          className={`d-flex ${justifyClass} align-items-center w-100 ageo-semibold ${
            field.name === "create-account-btn" && "btn-full"
          }`}
        >
          {field.linkName && (
          <a href={isSafeUrl(field.href) ? field.href : '#'} className="small-link">
            {field.linkName}
          </a>
        )}
          <div
            className={`${
              ["btn-reset-password", "btn-create-account", "btn-phlebotomist","continue"].some((cls) =>
                field.className?.includes(cls),
              )
                ? ""
                : "w-100"
            } ${field.className?.includes("btn-reset-password") && "reset-btn"}`}
          >
            {field.aboveBtnMsg && <p>{field.aboveBtnMsg}</p>}
            {!field.btnGroup && (
          <Button
                type={field.type}
                size={field.size}
                className={`${field?.className} float-end ${field?.className?.includes("Confirm & Continue") ? "" : "w-100"}`}
                variant={field.variant}
                label={field.label}
                disabled={
                  (field.controlId === "personal-details-save" &&
                  !isFormDirty(props?.formikRef?.current))||(field?.disabled)
                }
                onClick={() => {
                  if (props.submitAction) {
                    props.submitAction.current = field.controlId;
                  }
                }}
              />
            )}
            {field.btnGroup?.map((fieldBtn) => (
              <Button
                key={fieldBtn.controlId}
                type={fieldBtn.type}
                size={fieldBtn.size}
                className={`${fieldBtn?.className} float-end ${field?.className?.includes("Confirm & Continue") ? "" : "w-100"}`}
                variant={fieldBtn.variant}
                label={fieldBtn.label}
              />
            ))}
          </div>
        </div>
        <div>
          {field.links && (
            <span className="d-flex justify-content-center">{field.links}</span>
          )}
        </div>
      </>
    );
  }

  /**
   * Render button group field
   * @returns {JSX.Element} - Button group field component
   */

  function renderButtonGroupField(field, props, auth) {
    let className = "d-flex flex-wrap align-items-center";

    if (props.btnSecondary) {
      className = "d-flex flex-wrap align-items-center barcode-2-btns";
    } else if (props.genderButton) {
      className =
        "d-flex flex-wrap align-items-center btn-group toggle-btn-group";
    }

    return (
      <div>
        <div className={"form-group " + field?.className}>
          {field.label && (
            <Form.Label className="labels">
              {field.required ? (
                <>
                  {field.label}
                  <span className="required"></span>
                </>
              ) : (
                field.label
              )}
            </Form.Label>
          )}
          <div className={className}>
            {field?.buttons?.map((button) => (
              <>
                {button.aboveBtnMsg && (
                  <>
                    <p>{button?.aboveBtnMsg}</p> <br />
                  </>
                )}
                <Button
                  key={button.controlId}
                  type={button.type}
                  size={button.size}
                  className={button.className}
                  variant={button.variant}
                  label={button.label}
                  href={isSafeUrl(button.href) ? button.href : '#'}
                  active={button.active}
                  onClick={
                    button.label === "Previous step"
                      ? () => auth.handlePreviousStep(undefined, undefined, props?.isAssessmentCompletedForPhlebotomy)
                      : button.onClick
                  }
                />
              </>
            ))}
          </div>
          {field.helpText && (
            <small className="form-text text-muted mt-2">
              {field.helpText}
            </small>
          )}
        </div>
      </div>
    );
  }

  /**
   * Render toggle button field
   * @returns {JSX.Element}
   */

  function renderToggleButtonField(
    field,
    touched,
    errors,
    setFieldValue,
    values,
  ) {
    const error =
      typeof errors?.[field.name] === "string" ? errors[field.name] : undefined;
    const fieldValue = values?.[field.name];

    const getToggleValue = () => {
      if (field.name === "heightUnits") return heightUnitValue;
      if (field.name === "weightUnits") return weightUnitValue;
      return values?.[field.name] || toggleValue;
    };

    return (
      <div className="form-group toggle-btn-group">
        <ToggleButtons
          field={field}
          toggleValueProp={getToggleValue()}
          genderIdentityProp={genderIdentity}
          setFieldValue={setFieldValue}
          errors={error}
          touched={touched}
        />
        {(field.controlId === "gender" || field.controlId === "confirmation") &&
          errors[field.name] &&
          touched[field.name] && (
            <div className="invalid-feedback d-block">{errors[field.name]}</div>
          )}

        {field.helpText && (
          <small
            className={`form-text mt-2 ${
              field.className === "toggle-btn-group help-text"
                ? "create-account-helptext"
                : "text-muted"
            }`}
          >
            {field.helpText}
          </small>
        )}
      </div>
    );
  }

  /**
   * Render sample collection field
   * @returns {JSX.Element}
   */

  function renderSampleCollectionField(
    field,
    props,
    values,
    touched,
    errors,
    handleChange,
  ) {
    return (
      <div className="row date-time-administered" key={field.controlId}>
        {props.fields
          .filter(
            (f) =>
              f.name === "sample_collection_date" ||
              f.name === "sample_collection_time",
          )
          .map((f) => (
            <FormGroup
              className={`col-md-${f.colSize} ${f.className || "" }`}
              key={f.controlId}
            >
              <FormInput
                id={`input-${f.controlId}`}
                key={f.controlId}
                min={previousFifteenDays}
                max={todayFormatted}
                maxlength={f.maxLength}
                className="form-group"
                type={f.type}
                {...f}
                name={f.name}
                value={values[f.name]}
                helpTextMarkdown={f.helpTextMarkdown}
                onChange={handleChange}
                toggleValueProp={toggleValue}
                error={errors[f.name]}
                touched={!!touched[f.name] || errors[f.name]}
                validationError={props.validationError}
                setShowModal={props.setShowModal}
              />
            </FormGroup>
          ))}
      </div>
    );
  }

  /**
   * Render consent field
   * @returns {JSX.Element}
   */

  function renderConsentField(field, props) {
    return (
      <FormInput
        key={field.controlId}
        type="checkbox"
        name={field.name}
        id={field.controlId}
        heading={field.heading}
        message={field.message}
        checked={field.checked[field.value]}
        onChange={field.onChange}
        validationError={props.validationError}
      />
    );
  }

  /**
   * Render default field
   * @returns {JSX.Element}
   */

  function renderDefaultField(
    field,
    values,
    touched,
    errors,
    handleChange,
    props,
    setFieldValue,
  ) {
    if (field.check_box_group || field.btnGroup) {
      return field.h1 ? <h1 className="my-4">{field.h1}</h1> : null;
    }
    const resolvedOptions =
      field.name === "home_street_address"
        ? props.addressOptions
        : field.options;

    // Special handling for standalone checkboxes
    if (field.type === "checkbox" && !field.check_box_group) {
      return (
        <>
          <h1 className="my-4">{field.h1}</h1>
          <div
            className={
              touched[field.name] &&
              errors[field.name] &&
              "create-consent-error"
            }
          >
            <Form.Check
              id={`input-${field.controlId}`}
              key={field.controlId}
              type="checkbox"
              label={field.label}
              name={field.name}
              checked={values[field.name] || false}
              onChange={(e) => {
                const syntheticEvent = {
                  target: {
                    name: field.name,
                    value: e.target.checked,
                    type: "checkbox",
                    checked: e.target.checked,
                  },
                };

                if (props?.handleChange) {
                  props.handleChange(syntheticEvent, setFieldValue);
                } else {
                  setFieldValue(field.name, e.target.checked);
                }
              }}
              isInvalid={!!errors[field.name] && !!touched[field.name]}
              feedback={errors[field.name]}
              className={field.className || ""}
              disabled={field.disabled}
            />
            {["retest_instructions", "understand_refund_consent"].includes(
              field.name,
            ) ? (
              <>
                {touched[field.name] && errors[field.name] && (
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-block ageo-medium"
                  >
                    {errors[field.name]}
                  </Form.Control.Feedback>
                )}

                {field.helpText && (
                  <small
                    className={`form-text text-muted mt-1 ${field?.helpTextClass}`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(field.helpText),
                    }}
                  />
                )}
              </>
            ) : (
              <>
                {field.helpText && (
                  <small
                    className={`form-text text-muted mt-1 ${field?.helpTextClass}`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(field.helpText),
                    }}
                  />
                )}

                {touched[field.name] && errors[field.name] && (
                  <Form.Control.Feedback
                    type="invalid"
                    className="d-block ageo-medium"
                  >
                    {errors[field.name]}
                  </Form.Control.Feedback>
                )}
              </>
            )}
          </div>
        </>
      );
    }
    return (
      <>
        {field.h1 && <h1 className="my-4">{field.h1}</h1>}
        <FormInput
          links={field.links}
          id={`input-${field.controlId}`}
          key={field.controlId}
          className="form-group"
          type={field.type}
          {...field}
          name={field.name}
          maxLength={field.maxLength}
          value={values[field.name]}
          options={resolvedOptions}
          disabled={
            field.disabled ||
            (field.name === "home_street_address" &&
              (!values.home_postal_code || props.addressLoading))
          }
          helpTextMarkdown={field.helpTextMarkdown}
          toggleValueProp={toggleValue}
          onChange={(e) => {
            props?.handleChange?.(e, setFieldValue);
            handleChange(e);
            if (field.type === "password") {
              props.setPassword?.(e.target.value);
            }
          }}
          onBlur={(e) => {
            if (
              (field.name === "code" || field.name === "home_postal_code") &&
              props.handleCodeBlur
            ) {
              props.handleCodeBlur(e.target.value, setFieldValue);
            }
          }}
          error={errors[field.name]}
          touched={touched[field.name] ? "true" : "false"}
          image={field.image}
          validationError={props.validationError}
        />
      </>
    );
  }

  /** Render button group */
  function renderButtonGroup(field, auth, props) {
    if (!field.btnGroup) {
      return null;
    }

    return (
      <div className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile && "flex-column-reverse"} `}>
        {field.btnGroup.map((btn, btnIndex) => (
          <Button
            key={btn.controlId || `${field.controlId}-btn-${btnIndex}`}
            type={btn.type}
            size={btn.size}
            className={btn?.className}
            variant={btn.variant}
            label={btn.label}
            onClick={
              btn.label === "Previous step"
                ? () => auth.handlePreviousStep(undefined, undefined, props?.isAssessmentCompletedForPhlebotomy)
                : btn?.onClick
            }
          />
        ))}
      </div>
    );
  }

  /** Render checkbox group */
  function renderCheckboxGroup(field, context) {
    const { values, touched, errors, setFieldValue, props } = context;
    if (!field.check_box_group || field.check_group_hide) {
      return null;
    }

    return (
      <div
        className={`form-group ${field.group_label === "checkboxes" && "row mb-4"}`}
      >
        {field.check_group_label && (
          <Form.Label className="labels">
            {field.check_group_label}
            <span className="required"></span>
          </Form.Label>
        )}

        {field.check_box_group.map((check) => (
          <FormInput
            id={`input-${check.controlId}`}
            key={check.controlId}
            className="form-group"
            type={check.type}
            {...check}
            name={check.name}
            value={values[check.label]}
            toggleValueProp={toggleValue}
            helpTextMarkdown={check.helpTextMarkdown}
            onChange={(e) => {
              handleCheckboxChange(e, check.name, values, setFieldValue);
              if (check.type === "password") {
                props.setPassword?.(e.target.value);
              }
            }}
            checked={values.unprotected_sex_type?.includes(props.label)}
            error={errors[check.name]}
            touched={touched[check.name]}
            noLabel={true}
            image={check.image}
            validationError={props.validationError}
          />
        ))}
        {errors?.unprotected_sex_type &&
          field.name === "unprotected_sex_type" && (
            <div className="invalid-feedback mb-2">
              {errors.unprotected_sex_type}
            </div>
          )}

        {errors?.potential_sti_exposure_type &&
          field.name === "potential_sti_exposure_type" && (
            <div className="invalid-feedback mb-2">
              {errors.potential_sti_exposure_type}
            </div>
          )}
        <div>
          {field.helpText && (
            <small className="form-text text-muted mt-2">
              {field.helpText}
            </small>
          )}
        </div>
      </div>
    );
  }
};

export default DynamicForm;

DynamicForm.defaultProps = {
  fields: [],
  validationSchema: null,
  onSubmit: undefined,
  btnSecondary: false,
  isResetForm: true,
  enableReinitializeValue:true
};

DynamicForm.propTypes = {
  validationError: PropTypes.string,
  password: PropTypes.string,
  setPassword: PropTypes.func,
  handleChange: PropTypes.func,
  validationSchema: PropTypes.object,
  formikRef: PropTypes.object,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      controlId: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      buttons: PropTypes.array,
      as: PropTypes.string,
      required: PropTypes.bool,
      placeholder: PropTypes.string,
      size: PropTypes.string,
      readOnly: PropTypes.bool,
      disabled: PropTypes.bool,
      aria_describedby: PropTypes.string,
      helpText: PropTypes.string,
      linkName: PropTypes.string,
      href: PropTypes.string,
      className: PropTypes.string,
      variant: PropTypes.string,
      FormFieldImage: PropTypes.node,
    }),
  ),
  onSubmit: PropTypes.func,
  initialValues: PropTypes.shape({
    heightUnits: PropTypes.string,
    weightUnits: PropTypes.string,
    units: PropTypes.string,
    sex: PropTypes.string,
    identify_with_gender: PropTypes.bool,
  }),
  modify: PropTypes.bool,
  className: PropTypes.string,
  blockRefresh: PropTypes.bool,
  btnSecondary: PropTypes.bool,
  genderButton: PropTypes.bool,
  label: PropTypes.string,
  handleCodeBlur: PropTypes.func,
  setShowModal: PropTypes.func,
  addressOptions: PropTypes.object,
  consentChecked: PropTypes.bool,
  addressLoading: PropTypes.bool,
  sex: PropTypes.string,
  enableReinitializeValue: PropTypes.bool,
  isResetForm: PropTypes.bool,
  submitAction: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
};
